import type { MoveAttachmentsJob, MoveAttachmentsLog } from '@atlas/shared';
import { listMoveAttachmentLogs, listMoveAttachments } from './callables.js';
import { poll, type Unsubscribe } from './poll.js';

export const PAGE_SIZE = 50;

/**
 * Every S3 destination a run writes to, in display order: documents
 * (`destPrefix`), the image slot (`imageDestPrefix` — for Bullhorn this carries
 * the companies/ tree), and, for Bullhorn, the certifications/ tree. Shared by
 * the run list and the detail modal so the two never drift.
 */
export function moveAttachmentsDestinationUris(job: MoveAttachmentsJob): string[] {
  const imagesOnly = job.imagesOnly === true;
  const includeImages = imagesOnly || job.includeImages === true;
  const uris: string[] = [];
  if (!imagesOnly) uris.push(`s3://${job.destBucket}/${job.destPrefix}`);
  if (includeImages) uris.push(`s3://${job.destBucket}/${job.imageDestPrefix}`);
  if (job.certDestPrefix) uris.push(`s3://${job.destBucket}/${job.certDestPrefix}`);
  return uris;
}

/**
 * The destinations a run actually writes files to — a tree is included only
 * when it has content. After the copy that's the verified object count
 * (`result.destObjects`); before it (dry run / awaiting confirm) it's the
 * inventory match count (`plan.matching`). This is why an empty Bullhorn
 * category (e.g. a firm whose 32-byte CertificationFileAttachment stub yields
 * no files) is left out — nothing was ever written to `certifications/`.
 */
export function moveAttachmentsWrittenDestinationUris(job: MoveAttachmentsJob): string[] {
  const written = (
    plan: { matching: number } | null | undefined,
    result: { destObjects: number; copied: number } | null | undefined,
  ): boolean => (result ? result.destObjects > 0 || result.copied > 0 : (plan?.matching ?? 0) > 0);

  const uris: string[] = [];
  if (written(job.plan, job.result)) {
    uris.push(`s3://${job.destBucket}/${job.destPrefix}`);
  }
  if (written(job.imagePlan, job.imageResult)) {
    uris.push(`s3://${job.destBucket}/${job.imageDestPrefix}`);
  }
  if (job.certDestPrefix && written(job.certPlan, job.certResult)) {
    uris.push(`s3://${job.destBucket}/${job.certDestPrefix}`);
  }
  return uris;
}

export function subscribeToMoveAttachments(
  onUpdate: (jobs: Array<MoveAttachmentsJob & { id: string }>) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return poll(
    async () => (await listMoveAttachments({ limit: PAGE_SIZE })).data.jobs,
    onUpdate,
    onError,
  );
}

export function subscribeToMoveAttachmentLogs(
  onUpdate: (logs: Array<MoveAttachmentsLog & { id: string }>) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return poll(
    async () => (await listMoveAttachmentLogs({ limit: PAGE_SIZE })).data.logs,
    onUpdate,
    onError,
  );
}
